import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CreditCard,
  ShieldCheck,
  KeyRound,
  MailWarning,
  MailCheck,
  LogOut,
  Download,
  Trash2,
  Loader2,
  Copy,
  AlertTriangle,
  Check,
} from "lucide-react";
import qrcode from "@/lib/qrcode";
import { API, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PLANOS, irParaPagamento, formatarData, STATUS_ASSINATURA } from "@/lib/planos";
import { useSuporte, linkWhatsApp } from "@/lib/suporte";

function erro(err, padrao = "Algo deu errado. Tente novamente.") {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : padrao;
}

async function abrirPortal() {
  const res = await axios.post(`${API}/billing/portal`, { origin_url: window.location.origin });
  window.location.href = res.data.url;
}

function StatusAssinatura({ a }) {
  if (!a) return null;
  if (a.vitalicio) return <Badge className="bg-teal-600">Acesso vitalício</Badge>;
  const nome = STATUS_ASSINATURA[a.status] || "Sem assinatura";
  const cor = a.tem_acesso ? (a.status === "past_due" ? "bg-amber-500" : "bg-teal-600") : "bg-slate-500";
  return <Badge className={cor}>{nome}</Badge>;
}

// ==================== ABA ASSINATURA ====================

function AbaAssinatura() {
  const [a, setA] = useState(null);
  const [abrindo, setAbrindo] = useState(false);

  useEffect(() => {
    axios.get(`${API}/billing/assinatura`).then((r) => setA(r.data)).catch(() => setA({}));
  }, []);

  const gerenciar = async () => {
    setAbrindo(true);
    try {
      await abrirPortal();
    } catch (err) {
      setAbrindo(false);
      toast.error(erro(err));
    }
  };

  if (!a) return <div className="flex h-32 items-center justify-center"><div className="spinner" /></div>;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-teal-600" />
          <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>Minha assinatura</CardTitle>
        </div>
        <CardDescription>Plano, próxima cobrança, cartão e recibos</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-2xl font-bold text-slate-900 dark:text-white">
            {a.vitalicio ? "Vitalício" : a.plano_nome ? `Plano ${a.plano_nome}` : "Nenhum plano"}
          </span>
          <StatusAssinatura a={a} />
        </div>
        {!a.vitalicio && a.periodo_fim && (
          <p className="text-slate-600 dark:text-slate-300">
            {a.cancelar_no_fim
              ? `Cancelada: o acesso termina em ${formatarData(a.periodo_fim)}.`
              : a.tem_acesso
              ? `Próxima renovação em ${formatarData(a.periodo_fim)}.`
              : `Venceu em ${formatarData(a.periodo_fim)}.`}
          </p>
        )}
        {a.status === "past_due" && (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            Não conseguimos cobrar a renovação. Atualize o cartão para não perder o acesso.
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          {a.pode_gerenciar && (
            <Button onClick={gerenciar} disabled={abrindo} className="bg-teal-600 hover:bg-teal-700" data-testid="gerenciar-assinatura">
              {abrindo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
              Gerenciar assinatura
            </Button>
          )}
          {!a.tem_acesso && (
            <Button asChild variant="outline"><Link to="/assinatura">Renovar</Link></Button>
          )}
        </div>
        {a.pode_gerenciar && (
          <p className="text-sm text-slate-500">
            No portal seguro do Stripe você troca o cartão, muda de plano, baixa os recibos e cancela quando quiser. Ao
            cancelar, o acesso continua até o fim do período pago.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== ABA SEGURANCA ====================

function QrCode({ texto }) {
  const src = useMemo(() => {
    const q = qrcode(0, "M");
    q.addData(texto);
    q.make();
    return q.createDataURL(5, 2);
  }, [texto]);
  return <img src={src} alt="QR code para o aplicativo autenticador" className="h-44 w-44 rounded border bg-white p-1" />;
}

function CodigosReserva({ codigos, onFechar }) {
  const texto = codigos.join("\n");
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success("Códigos copiados");
    } catch {
      toast.error("Não foi possível copiar. Anote os códigos.");
    }
  };
  const baixar = () => {
    const blob = new Blob([`Codigos de reserva - Aesthetica Pricing\nCada codigo vale uma vez.\n\n${texto}\n`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "codigos-reserva-aesthetica.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Guarde seus códigos de reserva</DialogTitle>
          <DialogDescription>
            Se perder o celular, cada código abaixo entra no lugar do aplicativo uma única vez. Eles não serão mostrados
            de novo.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-4 font-mono text-lg dark:bg-slate-800">
          {codigos.map((c) => <span key={c}>{c}</span>)}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex gap-2">
            <Button variant="outline" onClick={copiar}><Copy className="mr-2 h-4 w-4" />Copiar</Button>
            <Button variant="outline" onClick={baixar}><Download className="mr-2 h-4 w-4" />Baixar</Button>
          </div>
          <Button className="bg-teal-600 hover:bg-teal-700" onClick={onFechar}>Já guardei</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CartaoDoisFatores() {
  const { user, refreshUser, guardarToken } = useAuth();
  const [configuracao, setConfiguracao] = useState(null); // {segredo, otpauth_url}
  const [codigo, setCodigo] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [codigos, setCodigos] = useState(null);
  const [desativando, setDesativando] = useState(false);
  const [senha, setSenha] = useState("");
  const [codigoDesativar, setCodigoDesativar] = useState("");
  const [codigoNovos, setCodigoNovos] = useState("");

  const iniciar = async () => {
    setOcupado(true);
    try {
      const r = await axios.post(`${API}/auth/2fa/iniciar`);
      setConfiguracao(r.data);
      setCodigo("");
    } catch (err) {
      toast.error(erro(err));
    } finally {
      setOcupado(false);
    }
  };

  const ativar = async (e) => {
    e.preventDefault();
    setOcupado(true);
    try {
      const r = await axios.post(`${API}/auth/2fa/ativar`, { codigo });
      guardarToken(r.data.token);
      setConfiguracao(null);
      setCodigos(r.data.codigos_reserva);
    } catch (err) {
      toast.error(erro(err));
    } finally {
      setOcupado(false);
    }
  };

  const desativar = async (e) => {
    e.preventDefault();
    setOcupado(true);
    try {
      await axios.post(`${API}/auth/2fa/desativar`, { senha, codigo: codigoDesativar });
      toast.success("Verificação em duas etapas desativada");
      setDesativando(false);
      setSenha("");
      setCodigoDesativar("");
      await refreshUser();
    } catch (err) {
      toast.error(erro(err));
    } finally {
      setOcupado(false);
    }
  };

  const novosCodigos = async (e) => {
    e.preventDefault();
    setOcupado(true);
    try {
      const r = await axios.post(`${API}/auth/2fa/codigos-reserva`, { codigo: codigoNovos });
      setCodigoNovos("");
      setCodigos(r.data.codigos_reserva);
    } catch (err) {
      toast.error(erro(err));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Card id="dois-fatores">
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-teal-600" />
          <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>Verificação em duas etapas</CardTitle>
          {user?.totp_ativo ? <Badge className="bg-teal-600">Ativa</Badge> : <Badge variant="outline">Desligada</Badge>}
        </div>
        <CardDescription>
          Além da senha, o login pede um código do aplicativo autenticador do celular (Google Authenticator, Microsoft
          Authenticator ou similar).
          {user?.role === "admin" && " Para o administrador, é obrigatória."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {user?.precisa_2fa && (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            Ative a verificação em duas etapas para liberar o restante do sistema.
          </div>
        )}

        {!user?.totp_ativo && !configuracao && (
          <Button onClick={iniciar} disabled={ocupado} className="bg-teal-600 hover:bg-teal-700" data-testid="2fa-iniciar">
            {ocupado && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Ativar verificação em duas etapas
          </Button>
        )}

        {configuracao && (
          <form onSubmit={ativar} className="space-y-4">
            <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-600 dark:text-slate-300">
              <li>Abra o aplicativo autenticador e toque em adicionar conta.</li>
              <li>Escaneie o QR code. Sem câmera? Digite a chave abaixo.</li>
              <li>Digite o código de 6 números que aparecer.</li>
            </ol>
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
              <QrCode texto={configuracao.otpauth_url} />
              <div className="space-y-2 text-sm">
                <div className="text-slate-500">Chave para digitar:</div>
                <code className="block break-all rounded bg-slate-100 p-2 font-mono dark:bg-slate-800">{configuracao.segredo}</code>
              </div>
            </div>
            <div className="max-w-xs space-y-2">
              <Label htmlFor="codigo-ativar">Código do aplicativo</Label>
              <Input id="codigo-ativar" value={codigo} onChange={(e) => setCodigo(e.target.value)} inputMode="numeric"
                autoComplete="one-time-code" maxLength={6} placeholder="000000" className="font-mono text-lg tracking-widest"
                required data-testid="2fa-codigo-ativar" />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={ocupado} className="bg-teal-600 hover:bg-teal-700" data-testid="2fa-ativar">
                {ocupado && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirmar e ativar
              </Button>
              <Button type="button" variant="ghost" onClick={() => setConfiguracao(null)}>Cancelar</Button>
            </div>
          </form>
        )}

        {user?.totp_ativo && (
          <div className="space-y-5">
            <form onSubmit={novosCodigos} className="flex flex-wrap items-end gap-2">
              <div className="space-y-2">
                <Label htmlFor="codigo-novos">Gerar novos códigos de reserva</Label>
                <Input id="codigo-novos" value={codigoNovos} onChange={(e) => setCodigoNovos(e.target.value)}
                  placeholder="Código do aplicativo" inputMode="numeric" maxLength={6} className="w-48" required />
              </div>
              <Button type="submit" variant="outline" disabled={ocupado}>Gerar códigos</Button>
            </form>
            {user?.role !== "admin" && !desativando && (
              <Button variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => setDesativando(true)}>
                Desativar verificação em duas etapas
              </Button>
            )}
            {desativando && (
              <form onSubmit={desativar} className="space-y-3 rounded-lg border border-rose-200 p-4">
                <p className="text-sm text-slate-600 dark:text-slate-300">Para desligar, confirme a senha e um código do aplicativo.</p>
                <Input type="password" value={senha} onChange={(e) => setSenha(e.target.value)} placeholder="Senha" required autoComplete="current-password" />
                <Input value={codigoDesativar} onChange={(e) => setCodigoDesativar(e.target.value)} placeholder="Código do aplicativo ou de reserva" required />
                <div className="flex gap-2">
                  <Button type="submit" variant="destructive" disabled={ocupado}>Desativar</Button>
                  <Button type="button" variant="ghost" onClick={() => setDesativando(false)}>Cancelar</Button>
                </div>
              </form>
            )}
          </div>
        )}
      </CardContent>
      {codigos && (
        <CodigosReserva
          codigos={codigos}
          onFechar={async () => {
            setCodigos(null);
            await refreshUser();
          }}
        />
      )}
    </Card>
  );
}

function AbaSeguranca() {
  const { user, guardarToken, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [reenviando, setReenviando] = useState(false);

  const trocarSenha = async (e) => {
    e.preventDefault();
    if (nova.length < 8) return toast.error("A senha nova precisa ter pelo menos 8 caracteres");
    if (nova !== confirmacao) return toast.error("As senhas não coincidem");
    setSalvando(true);
    try {
      const r = await axios.post(`${API}/auth/change-password`, { current_password: atual, new_password: nova });
      guardarToken(r.data.token);
      // Tira o aviso da senha provisoria e, para admin, passa ao 2FA.
      await refreshUser();
      setAtual("");
      setNova("");
      setConfirmacao("");
      toast.success("Senha alterada. Os outros aparelhos foram desconectados.");
    } catch (err) {
      toast.error(erro(err));
    } finally {
      setSalvando(false);
    }
  };

  const reenviar = async () => {
    setReenviando(true);
    try {
      const r = await axios.post(`${API}/auth/reenviar-confirmacao`);
      toast.success(r.data.message);
    } catch (err) {
      toast.error(erro(err));
    } finally {
      setReenviando(false);
    }
  };

  const sairDeTodos = async () => {
    try {
      await axios.post(`${API}/auth/logout-todos`);
    } catch {}
    await logout();
    toast.success("Você saiu de todos os aparelhos");
    navigate("/login");
  };

  return (
    <div className="space-y-6">
      {user?.precisa_trocar_senha && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800" data-testid="aviso-trocar-senha">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          Você entrou com uma senha provisória. Crie a sua senha abaixo para liberar o sistema.
        </div>
      )}
      {user?.role !== "admin" && <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            {user?.email_verificado ? <MailCheck className="h-5 w-5 text-teal-600" /> : <MailWarning className="h-5 w-5 text-amber-500" />}
            <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>E-mail</CardTitle>
          </div>
          <CardDescription>{user?.email}</CardDescription>
        </CardHeader>
        <CardContent>
          {user?.email_verificado ? (
            <p className="text-sm text-slate-600 dark:text-slate-300">E-mail confirmado.</p>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-slate-600 dark:text-slate-300">Confirme seu e-mail para conseguir recuperar a senha.</p>
              <Button variant="outline" size="sm" onClick={reenviar} disabled={reenviando}>
                {reenviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Reenviar link
              </Button>
            </div>
          )}
        </CardContent>
      </Card>}

      {!user?.precisa_trocar_senha && <CartaoDoisFatores />}

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-teal-600" />
            <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>Trocar senha</CardTitle>
          </div>
          <CardDescription>Ao trocar, os outros aparelhos conectados saem da conta.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={trocarSenha} className="grid max-w-md gap-3">
            <Input type="password" value={atual} onChange={(e) => setAtual(e.target.value)} placeholder={user?.precisa_trocar_senha ? "Senha provisória" : "Senha atual"} required autoComplete="current-password" data-testid="senha-atual" />
            <Input type="password" value={nova} onChange={(e) => setNova(e.target.value)} placeholder="Senha nova (mínimo 8 caracteres)" required autoComplete="new-password" data-testid="senha-nova" />
            <Input type="password" value={confirmacao} onChange={(e) => setConfirmacao(e.target.value)} placeholder="Repita a senha nova" required autoComplete="new-password" />
            <Button type="submit" disabled={salvando} className="w-fit bg-teal-600 hover:bg-teal-700" data-testid="trocar-senha">
              {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salvar senha nova
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <LogOut className="h-5 w-5 text-teal-600" />
            <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>Aparelhos conectados</CardTitle>
          </div>
          <CardDescription>Esqueceu a conta aberta em outro computador? Saia de todos de uma vez.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={sairDeTodos}>Sair de todos os aparelhos</Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== ABA PRIVACIDADE ====================

function AbaPrivacidade() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [copias, setCopias] = useState([]);
  const [excluindo, setExcluindo] = useState(false);
  const [senha, setSenha] = useState("");
  const [codigo, setCodigo] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    axios.get(`${API}/backups`).then((r) => setCopias(r.data)).catch(() => {});
  }, []);

  const excluir = async (e) => {
    e.preventDefault();
    setOcupado(true);
    try {
      await axios.post(`${API}/conta/excluir`, { senha, codigo, confirmacao });
      await logout();
      toast.success("Conta excluída. Sentiremos sua falta!");
      navigate("/");
    } catch (err) {
      toast.error(erro(err));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Download className="h-5 w-5 text-teal-600" />
            <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>Seus dados</CardTitle>
          </div>
          <CardDescription>Baixe tudo o que você cadastrou, quando quiser.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button asChild variant="outline">
            <a href={`${API_BASE}/api/export/backup`} download>
              <Download className="mr-2 h-4 w-4" />Exportar meus dados agora
            </a>
          </Button>
          <div>
            <div className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">Cópias automáticas (uma por dia, quando há mudança)</div>
            {copias.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhuma cópia ainda. A primeira é feita no próximo backup diário.</p>
            ) : (
              <ul className="divide-y rounded-lg border dark:divide-slate-800 dark:border-slate-800">
                {copias.map((c) => (
                  <li key={c.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span>{new Date(c.criado_em).toLocaleString("pt-BR")}</span>
                    <a className="text-teal-600 hover:underline" href={`${API}/backups/${c.id}`} download>Baixar</a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      {user?.role !== "admin" && (
        <Card className="border-rose-200">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-rose-600" />
              <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>Excluir conta</CardTitle>
            </div>
            <CardDescription>
              Apaga sua conta e todos os seus dados e cancela a assinatura na hora. Não dá para desfazer. Se quiser, baixe
              seus dados antes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!excluindo ? (
              <Button variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50" onClick={() => setExcluindo(true)}>
                Quero excluir minha conta
              </Button>
            ) : (
              <form onSubmit={excluir} className="grid max-w-md gap-3">
                <Input type="password" value={senha} onChange={(e) => setSenha(e.target.value)} placeholder="Sua senha" required autoComplete="current-password" />
                {user?.totp_ativo && (
                  <Input value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder="Código do aplicativo autenticador" required />
                )}
                <Label htmlFor="confirma-excluir" className="text-sm">Digite <strong>EXCLUIR</strong> para confirmar</Label>
                <Input id="confirma-excluir" value={confirmacao} onChange={(e) => setConfirmacao(e.target.value)} required data-testid="confirma-excluir" />
                <div className="flex gap-2">
                  <Button type="submit" variant="destructive" disabled={ocupado || confirmacao.trim().toUpperCase() !== "EXCLUIR"} data-testid="excluir-conta">
                    {ocupado && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Excluir definitivamente
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setExcluindo(false)}>Cancelar</Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ==================== PAGINA MINHA CONTA ====================

export default function AccountPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const aba = params.get("aba") || (user?.precisa_trocar_senha || user?.precisa_2fa ? "seguranca" : "assinatura");

  return (
    <div className="space-y-6 animate-slide-in" data-testid="account-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "Manrope" }}>Minha conta</h1>
        <p className="text-slate-500 mt-1">{user?.name} · {user?.email}</p>
      </div>
      <Tabs value={aba} onValueChange={(v) => setParams({ aba: v }, { replace: true })}>
        <TabsList>
          {user?.role !== "admin" && <TabsTrigger value="assinatura">Assinatura</TabsTrigger>}
          <TabsTrigger value="seguranca">Segurança</TabsTrigger>
          <TabsTrigger value="privacidade">Meus dados</TabsTrigger>
        </TabsList>
        {user?.role !== "admin" && <TabsContent value="assinatura" className="mt-6"><AbaAssinatura /></TabsContent>}
        <TabsContent value="seguranca" className="mt-6"><AbaSeguranca /></TabsContent>
        <TabsContent value="privacidade" className="mt-6"><AbaPrivacidade /></TabsContent>
      </Tabs>
    </div>
  );
}

// ==================== RENOVAR (assinatura vencida) ====================

export function SubscriptionPage() {
  const { user, loading, logout } = useAuth();
  const suporte = useSuporte(!!user);
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [carregando, setCarregando] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    axios.get(`${API}/billing/assinatura`).then((r) => setA(r.data)).catch(() => setA({}));
  }, [user, loading, navigate]);

  const assinar = async (plano) => {
    setCarregando(plano);
    try {
      await irParaPagamento(plano);
    } catch (err) {
      setCarregando(null);
      toast.error(erro(err, "Não foi possível abrir o pagamento."));
    }
  };

  const portal = async () => {
    setCarregando("portal");
    try {
      await abrirPortal();
    } catch (err) {
      setCarregando(null);
      toast.error(erro(err));
    }
  };

  if (loading || !a) return <div className="flex h-screen items-center justify-center"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 dark:bg-slate-950">
      <div className="mx-auto max-w-4xl">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-teal-700 dark:text-teal-400" style={{ fontFamily: "Manrope" }}>Aesthetica</h1>
          <p className="text-sm text-slate-500">Pricing Pro</p>
        </div>
        {a.tem_acesso ? (
          <Card className="mx-auto mt-8 max-w-md text-center">
            <CardContent className="space-y-4 pt-8 pb-8">
              <Check className="mx-auto h-12 w-12 text-green-600" />
              <p className="text-lg font-medium">Sua assinatura está ativa.</p>
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => navigate("/")}>Ir para o app</Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="mx-auto mt-8 max-w-2xl">
              <CardContent className="space-y-3 pt-6">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Olá, {user?.name?.split(" ")[0]}! Sua assinatura não está ativa.</h2>
                <p className="text-slate-600 dark:text-slate-300">
                  {a.status === "past_due" || a.status === "unpaid"
                    ? "A cobrança da renovação não passou. Atualize o cartão para voltar a usar o app."
                    : a.periodo_fim
                    ? `O acesso terminou em ${formatarData(a.periodo_fim)}. Seus dados continuam guardados: escolha um plano para voltar.`
                    : "Escolha um plano para liberar o acesso. Seus dados continuam guardados."}
                </p>
                {a.pode_gerenciar && (a.status === "past_due" || a.status === "unpaid") && (
                  <Button onClick={portal} disabled={carregando !== null} className="bg-teal-600 hover:bg-teal-700">
                    {carregando === "portal" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Atualizar cartão
                  </Button>
                )}
              </CardContent>
            </Card>
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {PLANOS.map((p) => (
                <Card key={p.id} className={p.destaque ? "border-teal-600 shadow-lg" : ""}>
                  <CardContent className="space-y-3 pt-6">
                    <div className="text-lg font-semibold">{p.nome}</div>
                    <div><span className="text-3xl font-bold">{p.precoTexto}</span><span className="text-slate-500">{p.periodo}</span></div>
                    <div className="h-5 text-sm text-teal-700">{p.porMes ? `${p.porMes} · ${p.economia}` : ""}</div>
                    <Button onClick={() => assinar(p.id)} disabled={carregando !== null} className="w-full bg-teal-600 hover:bg-teal-700" data-testid={`renovar-${p.id}`}>
                      {carregando === p.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Assinar {p.nome}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm">
              <a className="text-teal-700 hover:underline" href={`${API_BASE}/api/export/backup`} download>Baixar meus dados</a>
              <Link className="text-teal-700 hover:underline" to="/conta?aba=privacidade">Excluir conta</Link>
              {suporte && <a className="text-teal-700 hover:underline" href={linkWhatsApp(suporte, user)} target="_blank" rel="noreferrer">Falar com o suporte</a>}
              <button className="text-slate-500 hover:underline" onClick={async () => { await logout(); navigate("/"); }}>Sair</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
